import i18nFactory from '@yandex-int/i18n';
import { compact } from 'lodash';
import * as React from 'react';

import { BannerPreview } from '../../components/BannerPreview/BannerPreview';
import { EditablePhraseCell } from '../../components/EditablePhraseCell/EditablePhraseCell';
import { GridCampaignTypeCell } from '../../components/GridCampaignTypeCell/GridCampaignTypeCell';
import { GridESSStatusCellContainer } from '../../components/GridESSStatusCell/GridESSStatusCell';
import { GridESSStatusCellJSONContainer } from '../../components/GridESSStatusCellJSON/GridESSStatusCellJSON';
import { GridParentCell } from '../../components/GridParentCell/GridParentCell';
import { MoneyCell } from '../../components/MoneyCell/MoneyCell';
import { PhraseContextCoverageCell } from '../../components/PhraseContextCoverageCell/PhraseContextCoverageCell';
import { PhraseStatusCell } from '../../components/PhraseStatusCell/PhraseStatusCell';
import { PhraseTrafficForecast } from '../../components/PhraseTrafficFocast/PhraseTrafficForecast';
import { TextCell } from '../../components/TextCell/TextCell';
import { EditableAutobudgetPriorityCellContainer } from '../../containers/EditableAutobudgetPriorityCellContainer/EditableAutobudgetPriorityCellContainer';
import { GridBannerPreviewCellContainer } from '../../containers/GridBannerPreviewCellContainer/GridBannerPreviewCellContainer';
import { PhraseAutoStrategyPriceInfoContainer } from '../../containers/PhraseAutoStrategyPriceInfoContainer/PhraseAutoStrategyPriceInfoContainer';
import { PhraseMoneyCellContextEditorContainer } from '../../containers/PhraseMoneyCellContextEditorContainer/PhraseMoneyCellContextEditorContainer';
import { PhraseMoneyCellSimpleEditorContainer } from '../../containers/PhraseMoneyCellSimpleEditorContainer/PhraseMoneyCellSimpleEditorContainer';
import { PhraseMoneyCellTrafaretAuctionEditorContainer } from '../../containers/PhraseMoneyCellTrafaretAuctionEditorContainer/PhraseMoneyCellTrafaretAuctionEditorContainer';
import { PhraseTrafficForecastCellContainer } from '../../containers/PhraseTrafficForecastCellContainer/PhraseTrafficForecastCellContainer';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { isTextBannerCheck } from '../../service/BannerHelpers/BannerHelpers';
import { Bem } from '../../service/Bem/Bem';
import { addSettingsToDecl, arrangeInOrder } from '../../service/DeclarationHelper';
import { isStatusTextVisible } from '../../service/GridHelpers/GridHelpers';
import {
    getTrafaretsAuctionDataByParamValue,
    isPhraseEditable,
    mapAutobudgetPriorityToString
} from '../../service/PhrasesHelpers/PhrasesHelpers';
import { ColumnsSettings, ColumnsSettingsViewDict, phrases } from '../../typings/common';
import { ConstData } from '../../typings/constants';
import { DocCenterColumnTooltip } from '../../typings/doc-center';
import { Phrase } from '../../typings/domain';
import { CurrencyCode, GdShowConditionType } from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnDecl,
    GridColumnsOptions,
    GridPhraseItemActions,
    GridScreenTotalKey,
    OptionalGridColumnDecl,
    SortDirection
} from '../../typings/grid';
import { getDefaultGoalsViewInfo, getGoalsColumnsDecl } from '../goals/goals';
import { getDynamicStatsColumnsFields, getStaticStatsColumns } from '../stats/stats';

import * as keyset from './phrase.i18n';

const i18n = i18nFactory(keyset);

type PhraseGridColumnsOptions = GridColumnsOptions & {
    isSelectedSeveralGroups: boolean;
};

export enum PhraseColumnKey {
    keyword = 'keyword',
    status = 'status',
    campaignType = 'campaign-type',
    campaignId = 'campaign_id',
    groupId = 'group_id',
    price = 'price',
    trafficForecast = 'traffic-forecast',
    amnestyPrice = 'amnesty-price',
    contextPrice = 'context-price',
    coverage = 'coverage',
    autobudgetPriority = 'autobudget_priority',
    aggregatedStatus = 'aggregated-status',
    aggregatedStatusInfo = 'aggregated-status-info'
    // убрано в DIRECT-81351
    // задача на возврат DIRECT-81672
    // ectr = 'ectr'
}

const getFixedPhraseColumns = (): Array<
    OptionalGridColumnDecl<Phrase, GridPhraseItemActions, PhraseGridColumnsOptions>
> => {
    return [
        {
            title: i18n('phrase'),
            key: PhraseColumnKey.keyword,
            fixed: true,
            mandatory: true,
            totalKey: GridScreenTotalKey.name,
            isAlignedToLeft: true,
            minWidth: 300,
            maxWidth: 700,
            CellComponent: React.memo(
                ({ rowData: phraseInstance }: GridCellProps<Phrase>): JSX.Element => {
                    const {
                        id,
                        type,
                        phrase,
                        minusWords,
                        groupId,
                        group: {
                            regionIds,
                            minusKeywords,
                            campaign: { id: campaignId },
                            type: groupType
                        }
                    } = phraseInstance;

                    const isEditable = isPhraseEditable(phraseInstance) && type !== GdShowConditionType.RELEVANCE_MATCH;

                    return (
                        <EditablePhraseCell
                            {...{
                                id,
                                minusWords,
                                campaignId,
                                groupId,
                                groupType,
                                key: id,
                                phrase,
                                regionIds,
                                groupMinusKeywords: minusKeywords,
                                isEditable,
                                columnName: 'phrase'
                            }}
                        />
                    );
                }
            )
        }
    ];
};

export const getPhraseColumnsBase = (
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    columnsSettings: ColumnsSettings,
    isCpmBanner: boolean,
    isAggregatedStatusAllowed: boolean,
    constants?: ConstData
): Array<OptionalGridColumnDecl<Phrase, GridPhraseItemActions, PhraseGridColumnsOptions>> => {
    const currency = mapStringToCurrencySymbol(clientCurrency);

    return compact([
        {
            title: i18n('status'),
            isSortable: false,
            key: PhraseColumnKey.status,
            isAlignedToLeft: true,
            minWidth: 35,
            maxWidth: 300,
            CellComponent: React.memo(
                ({ rowData: { status } }: GridCellProps<Phrase>): JSX.Element => {
                    const columnViewInfo = columnsSettings.columnViewInfo[PhraseColumnKey.status];

                    return (
                        <PhraseStatusCell
                            status={status}
                            isTextVisible={isStatusTextVisible(isCompactViewEnabled, columnViewInfo)}
                        />
                    );
                }
            )
        },
        isAggregatedStatusAllowed && {
            title: 'New status',
            key: PhraseColumnKey.aggregatedStatus,
            isSortable: false,
            minWidth: 35,
            maxWidth: 1000,
            CellComponent: React.memo(
                ({ rowData: { aggregatedStatus } }: GridCellProps<Phrase>): JSX.Element => {
                    return <GridESSStatusCellJSONContainer aggregatedStatus={aggregatedStatus} />;
                }
            )
        },
        isAggregatedStatusAllowed && {
            title: 'New status2',
            key: PhraseColumnKey.aggregatedStatusInfo,
            isSortable: false,
            minWidth: 35,
            maxWidth: 300,
            CellComponent: React.memo(
                ({ rowData: { aggregatedStatusInfo } }: GridCellProps<Phrase>): JSX.Element | null => {
                    if (!aggregatedStatusInfo) {
                        return null;
                    }

                    return (
                        <GridESSStatusCellContainer
                            status={aggregatedStatusInfo.status}
                            reasons={aggregatedStatusInfo.reasons}
                            counters={aggregatedStatusInfo.counters}
                        />
                    );
                }
            )
        },
        {
            title: i18n('campaign-type'),
            key: PhraseColumnKey.campaignType,
            defaultSortDirection: SortDirection.asc,
            isSortable: false,
            minWidth: 35,
            maxWidth: 105,
            align: 'right',
            CellComponent: React.memo(
                ({
                    rowData: {
                        group: {
                            campaign: { type: campaignType }
                        }
                    }
                }: GridCellProps<Phrase>): JSX.Element => {
                    return <GridCampaignTypeCell type={campaignType} />;
                }
            )
        },
        {
            title: i18n('campaign-group'),
            key: PhraseColumnKey.campaignId,
            isAlignedToLeft: true,
            minWidth: 300,
            maxWidth: 600,
            isAvailable: ({ isSelectedSeveralGroups }: PhraseGridColumnsOptions): boolean => {
                return isSelectedSeveralGroups;
            },
            CellComponent: React.memo(
                ({ rowData: { group }, rowIndex, actions }: GridCellProps<Phrase>): JSX.Element => (
                    <GridParentCell campaign={group.campaign} group={group} />
                )
            )
        },
        {
            title: i18n('banners'),
            // По key происходит сортировка. Договорились сортировать эту колонку по id группы.
            // Сейчас нельзя отдельно задать key и правило сортировки.
            key: PhraseColumnKey.groupId,
            isAlignedToLeft: true,
            minWidth: 445,
            maxWidth: 500,
            CellComponent: React.memo(
                ({ rowData: { mainBanner, bannersTotalCount, id } }: GridCellProps<Phrase>): JSX.Element => {
                    if (!mainBanner) {
                        return <TextCell>-</TextCell>;
                    }

                    const {
                        bannerType,
                        group: {
                            id: groupId,
                            campaign: { id: campaignId }
                        }
                    } = mainBanner;
                    const isTextBanner = isTextBannerCheck(bannerType);

                    return (
                        <GridBannerPreviewCellContainer
                            id={id}
                            campaignId={campaignId}
                            groupId={groupId}
                            bannersTotalCount={bannersTotalCount}
                            isTextBanner={isTextBanner}
                        >
                            <BannerPreview bannerPreviewData={mainBanner} />
                        </GridBannerPreviewCellContainer>
                    );
                }
            )
        },
        {
            title: i18n('price-with-currency', { currency }),
            titleSettings: i18n('price'),
            key: PhraseColumnKey.price,
            align: 'left',
            tooltipKey: DocCenterColumnTooltip.bid,
            isAlignedToLeft: true,
            minWidth: 90,
            maxWidth: 200,
            CellComponent: React.memo(
                ({ rowData }: GridCellProps<Phrase>): JSX.Element => {
                    const {
                        id,
                        groupId,
                        price,
                        group: {
                            campaign: {
                                strategy: { isAutoBudget }
                            }
                        },
                        priceEditable,
                        trafficVolumeData,
                        hasPhraseTrafaretAuction,
                        isSearchVisibility
                    } = rowData;

                    if (isAutoBudget && trafficVolumeData && isSearchVisibility) {
                        return <PhraseAutoStrategyPriceInfoContainer bid={price} />;
                    } else if (!priceEditable || !constants) {
                        return <MoneyCell num={price} />;
                    } else if (hasPhraseTrafaretAuction && trafficVolumeData) {
                        return (
                            <PhraseMoneyCellTrafaretAuctionEditorContainer
                                constants={constants}
                                id={id}
                                groupId={groupId}
                                currency={clientCurrency}
                                key={id}
                                bid={price}
                                trafficVolumeData={trafficVolumeData}
                                cellName={'price'}
                            />
                        );
                    } else {
                        return (
                            <PhraseMoneyCellSimpleEditorContainer
                                constants={constants}
                                bid={price}
                                id={id}
                                groupId={groupId}
                                key={id}
                                currency={clientCurrency}
                                cellName={'price'}
                            />
                        );
                    }
                }
            )
        },
        {
            title: i18n('traffic-forecast'),
            isSortable: false,
            key: PhraseColumnKey.trafficForecast,
            tooltipKey: DocCenterColumnTooltip.trafficForecast,
            isAlignedToLeft: true,
            minWidth: 150,
            maxWidth: 400,
            isAvailable: (options: PhraseGridColumnsOptions): boolean => {
                return !options.hasOnlyCpmCampaigns;
            },
            CellComponent: React.memo(
                ({ rowData: phrase }: GridCellProps<Phrase>): JSX.Element => {
                    const {
                        id,
                        groupId,
                        price,
                        group: {
                            campaign: {
                                strategy: { isAutoBudget }
                            }
                        },
                        trafficVolumeData,
                        priceEditable,
                        hasPhraseTrafaretAuction
                    } = phrase;

                    const trafaretsAuctionData =
                        (isAutoBudget || hasPhraseTrafaretAuction) &&
                        price !== undefined &&
                        trafficVolumeData !== undefined &&
                        getTrafaretsAuctionDataByParamValue('bid', price, trafficVolumeData);

                    if (trafficVolumeData) {
                        return (
                            <PhraseTrafficForecast
                                value={trafaretsAuctionData ? trafaretsAuctionData.trafficVolume : undefined}
                                minValue={trafficVolumeData[0].trafficVolume}
                                maxValue={trafficVolumeData[trafficVolumeData.length - 1].trafficVolume}
                                disabled={true}
                            />
                        );
                    } else if (!priceEditable || price === undefined || trafficVolumeData === undefined || !constants) {
                        return <TextCell>–</TextCell>;
                    }

                    return (
                        <PhraseTrafficForecastCellContainer
                            constants={constants}
                            id={id}
                            groupId={groupId}
                            cellName={'traffic-forecast'}
                            bid={price}
                            currency={clientCurrency}
                            trafficVolumeData={trafficVolumeData}
                            key={id}
                        />
                    );
                }
            )
        },
        {
            title: i18n('amnesty-price-with-currency', { currency }),
            titleSettings: i18n('amnesty-price'),
            isSortable: false,
            key: PhraseColumnKey.amnestyPrice,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.clickPrice,
            minWidth: 90,
            maxWidth: 200,
            isAvailable: (): boolean => true,
            CellComponent: React.memo(
                ({ rowData: phrase }: GridCellProps<Phrase>): JSX.Element => {
                    const {
                        price,
                        group: {
                            campaign: {
                                strategy: { isAutoBudget }
                            }
                        },
                        trafficVolumeData,
                        hasPhraseTrafaretAuction
                    } = phrase;
                    const trafaretsAuctionData =
                        (isAutoBudget || hasPhraseTrafaretAuction) &&
                        price !== undefined &&
                        trafficVolumeData !== undefined &&
                        getTrafaretsAuctionDataByParamValue('bid', price, trafficVolumeData);

                    if (trafaretsAuctionData) {
                        return <MoneyCell num={trafaretsAuctionData.amnestyPrice} />;
                    }

                    return <TextCell>–</TextCell>;
                }
            )
        },
        {
            title: isCpmBanner
                ? i18n('context-price-with-currency-cpm-banner', { currency })
                : i18n('context-price-with-currency', { currency }),
            titleSettings: isCpmBanner ? i18n('context-price-cpm') : i18n('context-price'),
            key: PhraseColumnKey.contextPrice,
            sortKey: 'price_context',
            align: 'left',
            tooltipKey: isCpmBanner ? DocCenterColumnTooltip.cpmBid : DocCenterColumnTooltip.bidInNetworks,
            minWidth: 90,
            maxWidth: 200,
            CellComponent: React.memo(
                ({
                    rowData: {
                        id,
                        type,
                        groupId,
                        priceContext,
                        pokazometerData,
                        priceContextEditable,
                        isContextVisibility,
                        group: {
                            campaign: {
                                strategy: { isAutoBudget }
                            }
                        }
                    }
                }: GridCellProps<Phrase>): JSX.Element => {
                    if (type === GdShowConditionType.RELEVANCE_MATCH) {
                        if (priceContextEditable && constants) {
                            return (
                                <PhraseMoneyCellSimpleEditorContainer
                                    constants={constants}
                                    bid={priceContext}
                                    id={id}
                                    groupId={groupId}
                                    key={id}
                                    currency={clientCurrency}
                                    cellName={'priceContext'}
                                />
                            );
                        }

                        return <MoneyCell num={priceContext} cls={Bem.cls('phrases-context-price-cell')} />;
                    }

                    if (isAutoBudget && isContextVisibility) {
                        if (isCpmBanner) {
                            return <TextCell>{'–'}</TextCell>;
                        }
                        return <PhraseAutoStrategyPriceInfoContainer bid={priceContext} />;
                    } else if (!priceContextEditable || !constants || !pokazometerData) {
                        return <MoneyCell num={priceContext} cls={Bem.cls('phrases-context-price-cell')} />;
                    }

                    const { priceForCoverage } = pokazometerData;

                    return (
                        <PhraseMoneyCellContextEditorContainer
                            cls={Bem.cls('phrases-context-price-cell')}
                            id={id}
                            groupId={groupId}
                            cellName={'priceContext'}
                            priceContext={priceContext}
                            currency={clientCurrency}
                            priceForCoverage={priceForCoverage}
                            constants={constants}
                            isCpmBanner={isCpmBanner}
                        />
                    );
                }
            )
        },
        {
            title: i18n('coverage-with-percent'),
            key: PhraseColumnKey.coverage,
            isSortable: false,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.coverage,
            minWidth: 90,
            maxWidth: 200,
            isAvailable: (options: PhraseGridColumnsOptions): boolean => {
                return !options.hasOnlyCpmCampaigns;
            },
            CellComponent: React.memo(
                ({ rowData: { priceContext, pokazometerData } }: GridCellProps<Phrase>): JSX.Element => {
                    if (!pokazometerData || !priceContext) {
                        return <TextCell>{'–'}</TextCell>;
                    }
                    const { allCostsAndClicks, maxShows } = pokazometerData;

                    return (
                        <PhraseContextCoverageCell
                            allCostsAndClicks={allCostsAndClicks}
                            maxShows={maxShows}
                            priceContext={priceContext}
                        />
                    );
                }
            )
        },
        {
            title: i18n('priority'),
            key: PhraseColumnKey.autobudgetPriority,
            tooltipKey: DocCenterColumnTooltip.priority,
            minWidth: 90,
            maxWidth: 150,
            isAvailable: (options: PhraseGridColumnsOptions): boolean => {
                return !options.hasOnlyCpmCampaigns;
            },
            CellComponent: React.memo(
                ({ rowData: phrase }: GridCellProps<Phrase>): JSX.Element => {
                    const { id, groupId, autobudgetPriority, autobudgetPriorityEditable } = phrase;
                    const phraseIsEditable = isPhraseEditable(phrase);

                    if (!phraseIsEditable || !autobudgetPriorityEditable) {
                        return (
                            <TextCell cls={Bem.cls('phrases-autobudget-priority-cell')}>
                                {autobudgetPriority !== undefined
                                    ? mapAutobudgetPriorityToString(autobudgetPriority)
                                    : '–'}
                            </TextCell>
                        );
                    }
                    return (
                        <EditableAutobudgetPriorityCellContainer
                            id={id}
                            groupId={groupId}
                            autobudgetPriority={autobudgetPriority}
                            columnName={'autobudget_priority'}
                            cls={Bem.cls('phrases-autobudget-priority-cell')}
                        />
                    );
                }
            )
        }
        // убрано в DIRECT-81351
        // задача на возврат DIRECT-81672
        // {
        //     title: i18n('ectr'),
        //     isSortable: false,
        //     key: PhraseColumnKey.ectr,
        //     align: 'right',
        //     isAvailable: () => true,
        //     getCellContent: ({ hasPhraseTrafaretAuction, wCtr }: Phrase) => {
        //         if (hasPhraseTrafaretAuction) {
        //             return <TextCell>–</TextCell>;
        //         }
        //
        //         return <NumberCell num={wCtr} fractionalPartLength={2} />;
        //     }
        // }
    ]);
};

export const phrasesColumnOrder = [
    'keyword',
    'status',
    'aggregated-status',
    'aggregated-status-info',
    'campaign-type',
    'campaign_id',
    'group_id',
    // задача на возврат DIRECT-81672
    // 'ectr',
    'price',
    'amnesty-price',
    'context-price',
    'traffic-forecast',
    'coverage',
    'autobudget_priority',
    'GOALS',
    'STAT_COST',
    'STAT_GOALS',
    'STAT_REVENUE',
    'STAT_PROFITABILITY',
    'STAT_AVG_GOAL_COST',
    'STAT_AVG_CLICK_COST',
    'STAT_SHOWS',
    'STAT_CPM_PRICE',
    'STAT_CLICKS',
    'STAT_CTR',
    'STAT_CONVERSION_RATE',
    'STAT_BOUNCE_RATE'
];

export function getDefaultDynamicPhraseColumnsFields(): ColumnsSettings {
    return {
        columnOrder: phrasesColumnOrder,
        columnViewInfo: {
            keyword: {
                width: 450,
                widthCompact: 450,
                hidden: false
            },
            status: {
                width: 200,
                widthCompact: 200,
                hidden: false
            },
            'aggregated-status': {
                width: 250,
                widthCompact: 250,
                hidden: false
            },
            'aggregated-status-info': {
                width: 250,
                widthCompact: 250,
                hidden: false
            },
            'campaign-type': {
                width: 35,
                widthCompact: 35,
                hidden: false
            },
            campaign_id: {
                width: 300,
                widthCompact: 300,
                hidden: false
            },
            group_id: {
                width: 445,
                widthCompact: 445,
                hidden: false
            },
            // задача на возврат DIRECT-81672
            // ectr: {
            //     width: 100,
            //     widthCompact: 90,
            //     hidden: false
            // },
            price: {
                width: 145,
                widthCompact: 145,
                hidden: false
            },
            'amnesty-price': {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            'context-price': {
                width: 190,
                widthCompact: 190,
                hidden: false
            },
            'traffic-forecast': {
                width: 200,
                widthCompact: 200,
                hidden: false
            },
            coverage: {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            autobudget_priority: {
                width: 100,
                widthCompact: 100,
                hidden: false
            },
            ...getDefaultGoalsViewInfo(),
            ...getDynamicStatsColumnsFields()
        }
    };
}

export function getPhraseColumns(
    options: PhraseGridColumnsOptions,
    columnsSettings: ColumnsSettingsViewDict,
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    isGoalsStatEnabled: boolean,
    hasSelectedGoals: boolean,
    isCpmBanner: boolean,
    isAggregatedStatusAllowed: boolean,
    constants?: ConstData,
    clientLogin?: string,
    hasSelectedGoalsAtColumnsSettingsEditor?: boolean,
    isColumnsSettingsEditorLocation?: boolean
): Array<GridColumnDecl<Phrase>> {
    const fixedColumns = getFixedPhraseColumns().map(column => {
        return addSettingsToDecl(column, columnsSettings, phrases);
    });

    const result = getPhraseColumnsBase(
        clientCurrency,
        isCompactViewEnabled,
        columnsSettings[phrases],
        isCpmBanner,
        isAggregatedStatusAllowed,
        constants
    )
        .concat(getGoalsColumnsDecl<Phrase>(hasSelectedGoalsAtColumnsSettingsEditor, isColumnsSettingsEditorLocation))
        .concat(
            getStaticStatsColumns<Phrase>(
                {
                    getCurrency: (item: Phrase): CurrencyCode => item.group.campaign.currency
                },
                clientCurrency,
                isCompactViewEnabled,
                hasSelectedGoals,
                isGoalsStatEnabled,
                hasSelectedGoalsAtColumnsSettingsEditor,
                isColumnsSettingsEditorLocation
            )
        )
        .filter(column => {
            return column.isAvailable ? column.isAvailable(options) : true;
        })
        .map(column => {
            return addSettingsToDecl(column, columnsSettings, phrases);
        });

    return fixedColumns.concat(arrangeInOrder(result, columnsSettings[phrases].columnOrder, true));
}
